import type { StateCreator } from 'zustand';
import {
  setPatient,
  setPatientBasicInfo,
  setPatientCareInfo,
  setPatientContactDetails,
  setPatientIdentificationInfo,
  setPatientIhiVerification,
  setPatientRedFlagInfo,
  setPatientResidentialAddress,
} from './actions';
import {
  getPatient,
  getPatientBasicInfo,
  getPatientCareInfo,
  getPatientCode,
  getPatientContactDetails,
  getPatientId,
  getPatientIdentificationInfo,
  getPatientRedFlagInfo,
  getPatientResidentialAddress,
} from './selectors';
import type {
  BasicInfo,
  CareInfo,
  ContactDetails,
  IdentificationInfo,
  Patient,
  PatientSliceType,
  RedFlagInfo,
  ResidentialAddress,
} from './types';

export const patientSlice: StateCreator<
  PatientSliceType,
  [['zustand/immer', never], ['zustand/devtools', never]],
  [],
  PatientSliceType
> = (set, get, store) => ({
  patient: {
    patientId: null,
    patientCode: null,
    needsIhiVerification: false,
    basicInfo: {
      firstName: null,
      lastName: null,
      dob: null,
      gender: null,
      userActive: null,
      stateCode: null,
      dischargeStatus: null,
      userId: null,
      patientCode: null,
      showConsentFormResendLink: false,
    },
    careInfo: {
      doctor: {
        doctorId: null,
        active: null,
        address: null,
        email: null,
        firstName: null,
        lastName: null,
        phone: null,
        prescriberNumber: null,
      },
      gpClinic: null,
    },
    identificationInfo: {
      ihiNumber: null,
      ihiNumberStatus: null,
      ihiRecordStatus: null,
      medicareExpDate: null,
      medicareNumber: null,
      medicareRefNumber: null,
    },
    redFlagInfo: {
      hasRedFlag: false,
      redFlagComment: null,
    },
    contactDetails: {
      email: null,
      phone: null,
    },
    residentialAddress: {
      address: null,
      city: null,
      zipCode: null,
      stateId: null,
    },
  },
  getPatient: () => getPatient(get),
  getPatientId: () => getPatientId(get),
  getPatientCode: () => getPatientCode(get),
  getPatientBasicInfo: () => getPatientBasicInfo(get),
  getPatientCareInfo: () => getPatientCareInfo(get),
  getPatientResidentialAddress: () => getPatientResidentialAddress(get),
  getPatientIdentificationInfo: () => getPatientIdentificationInfo(get),
  getPatientRedFlagInfo: () => getPatientRedFlagInfo(get),
  getPatientContactDetails: () => getPatientContactDetails(get),
  setPatient: (attributes: Partial<Patient>) => setPatient(set, get, store)(attributes),
  setPatientBasicInfo: (attributes: BasicInfo) => setPatientBasicInfo(set, get, store)(attributes),
  setPatientCareInfo: (attributes: CareInfo) => setPatientCareInfo(set, get, store)(attributes),
  setPatientContactDetails: (attributes: ContactDetails) => setPatientContactDetails(set, get, store)(attributes),
  setPatientResidentialAddress: (attributes: ResidentialAddress) =>
    setPatientResidentialAddress(set, get, store)(attributes),
  setPatientIdentificationInfo: (attributes: IdentificationInfo) =>
    setPatientIdentificationInfo(set, get, store)(attributes),
  setPatientRedFlagInfo: (attributes: RedFlagInfo) => setPatientRedFlagInfo(set, get, store)(attributes),
  setPatientIhiVerification: (needsVerification: boolean) =>
    setPatientIhiVerification(set, get, store)(needsVerification),
});
